<template>
  <slot v-if="isAllowed === true" />
  <div v-else class="mx-4 mt-6 rounded-2xl border border-base-300 bg-base-100 p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <div class="max-w-3xl">
      <div v-if="badgeText !== ''" class="badge badge-outline">{{ badgeText }}</div>
      <h1 class="mt-4 text-2xl font-semibold text-gray-900 dark:text-gray-100">{{ titleText }}</h1>
      <p class="mt-3 text-sm leading-6 text-gray-600 dark:text-gray-300">{{ descriptionText }}</p>
      <div class="mt-6 flex flex-wrap items-center space-x-3 rtl:space-x-reverse">
        <div class="rounded-full border border-base-300 px-3 py-1 text-xs font-medium text-gray-700 dark:border-gray-600 dark:text-gray-200">
          {{ currentPlanText }}
        </div>
        <div class="rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-content">
          {{ requiredPlanText }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { globalState } from '@/globalState';

const props = defineProps({
  allowedSubscriptions: {
    type: Array,
    default: () => []
  },
  badge: {
    type: String,
    default: 'Enterprise feature'
  },
  title: {
    type: String,
    default: 'This page is not available for your subscription.'
  },
  description: {
    type: String,
    default: 'You need to upgrade to enterprise to use enterprise grade features. Contact your vendor to purchase it.'
  }
});

const normalizedAllowedSubscriptions = computed(() => {
  if (Array.isArray(props.allowedSubscriptions) !== true) {
    return [];
  }
  return props.allowedSubscriptions
    .map(value => typeof value === 'string' ? value.trim().toLowerCase() : '')
    .filter(value => value !== '');
});

const currentSubscription = computed(() => {
  const value = globalState.userInfo?.subscription;
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
});

const isAllowed = computed(() => {
  if (normalizedAllowedSubscriptions.value.length === 0) {
    return true;
  }
  if (currentSubscription.value === '') {
    return false;
  }
  return normalizedAllowedSubscriptions.value.includes(currentSubscription.value);
});

const titleText = computed(() => typeof props.title === 'string' ? props.title : '');
const descriptionText = computed(() => typeof props.description === 'string' ? props.description : '');
const badgeText = computed(() => typeof props.badge === 'string' ? props.badge : '');

function formatSubscriptionLabel(value) {
  if (typeof value !== 'string' || value === '') {
    return 'Unknown';
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

const currentPlanText = computed(() => `Current plan: ${formatSubscriptionLabel(currentSubscription.value)}`);
const requiredPlanText = computed(() => `Required plan: ${normalizedAllowedSubscriptions.value.map(formatSubscriptionLabel).join(', ')}`);
</script>
